import type { Styles } from '@adornis/ass/style.js';
import { type Maybe } from '@adornis/base/utilTypes.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { MatchingTimeframeAPI } from '../../api/MatchingTimeframeAPI.js';

@customElement('matching-prompt-confirm-cleanup')
export class MatchingPromptConfirmCleanup extends TrDialog<void> {
  static override get element_name() {
    return 'matching-prompt-confirm-cleanup';
  }

  @property({ attribute: false }) timeframe = new RXController<Maybe<Timeframe>>(this, undefined);

  protected async _runCleanup() {
    const timeframe = this.timeframe.value;

    if (timeframe) await MatchingTimeframeAPI.cleanupUsersAndMatches(timeframe)();

    this.close();
  }

  override render() {
    const timeframe = this.timeframe.value;
    if (!timeframe) {
      return html` <tr-loading-state no-wrapper></tr-loading-state> `;
    }

    return html`
      <tr-flex space="md" padding="xxs" flex>
        <tr-h3 bold> ${'Folgende Nutzerdaten werden gelöscht' /*this._translation.translate('PROMPT', '')*/} </tr-h3>

        <tr-text> Folgende Nutzerbezogene Daten werden <b>unwiderruflich</b> gelöscht: </tr-text>

        <tr-section horizontal space-between space="md">
          <tr-basic-card horizontal space="sm" padding="xs" crossaxis-center>
            <tr-icon> group_remove </tr-icon>
            <tr-text> Mentor </tr-text>
          </tr-basic-card>
          <tr-basic-card horizontal space="sm" padding="xs" crossaxis-center>
            <tr-icon> group_remove </tr-icon>
            <tr-text> Mentee </tr-text>
          </tr-basic-card>
          <tr-basic-card horizontal space="sm" padding="xs" crossaxis-center>
            <tr-icon> link_off </tr-icon>
            <tr-text> Matches </tr-text>
          </tr-basic-card>
        </tr-section>

        <tr-text> Die sich im folgenden Semester angemeldet haben: </tr-text>

        <tr-section horizontal space-between space="md">
          <tr-card flex ${css({ width: '100%' })}>
            <tr-flex horizontal space-between>
              <tr-h3>${timeframe.name}</tr-h3>

              <tr-flex horizontal space="xs" crossaxis-center>
                <tr-icon> ${timeframe.isActive ? 'wifi' : 'wifi_off'} </tr-icon>
                <tr-text>
                  ${timeframe.isActive
                    ? `Online`
                    : `${this._translation.translate('timeframe')} ${this._translation.translate(
                        'timeframe',
                        // @ts-ignore
                        'over',
                      )}`}
                </tr-text>
              </tr-flex>
            </tr-flex>

            <tr-flex horizontal space-between space="sm" flex wrap>
              <tr-text>von: ${timeframe.from.toLocaleString(DateTime.DATE_MED)}</tr-text>
              <tr-text>bis: ${timeframe.till.toLocaleString(DateTime.DATE_MED)}</tr-text>
              ${timeframe.publishDate
                ? html`<tr-text>
                    Veröffentlichung: ${timeframe.publishDate.toLocaleString(DateTime.DATE_MED)}
                  </tr-text>`
                : nothing}
            </tr-flex>
          </tr-card>
        </tr-section>

        <tr-flex horizontal space-between crossaxis-center space="md" wrap>
          <tr-button mode="outline" @click=${() => this.cancel()}>
            ${this._translation.translate('discard')}
          </tr-button>
          <tr-button @click=${() => this._runCleanup()}> ${this._translation.translate('approve')} </tr-button>
        </tr-flex>
      </tr-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        // selected
        'tr-flex[selected]': {
          background: this.colors.accent,
          borderRadius: this.sizes.borderRadius,
          color: '#fff',
        },
        'tr-flex[selected]:before': {
          content: "''",
          borderRadius: '50%',
          height: '10px',
          width: '10px',
          background: '#fff',
        },
        // synced
        'tr-flex[synced]': {
          background: this.colors.accent.shade(50),
          borderRadius: this.sizes.borderRadius,
          color: '#000',
        },
        // default
        'tr-flex[default]': {
          background: this.colors.tone.lightGrey,
          borderRadius: this.sizes.borderRadius,
          color: '#000',
        },
        'tr-flex[default]:before': {
          content: "''",
          borderRadius: '50%',
          height: '6px',
          width: '6px',
          border: `2px solid ${this.colors.accent}`,
        },
        'tr-flex[state]': {
          padding: `${this.spacing.xs} ${this.spacing.md}`,
          userSelect: 'none',
        },
        'tr-flex[clickable]': {
          cursor: 'pointer',
        },
      },
    ] as Styles[];
  }
}
